import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { session } from '../../client/session.js';
import { escapeAttribute, escapeHtml, validateEmail } from '../../shared/utils.js';
import componentStyles from './home-page.css?inline';
import { sanitizeMessageBody, sanitizePlainText } from '@/server/utils.js';

export class HomePage extends BaseComponent {
  private isSubmitting = false;
  private contactError = '';
  private contactSuccess = '';
  private contactName = '';
  private contactEmail = '';
  private contactMessage = '';

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    const isLoggedIn = session.isAuthenticated();

    return `
      <header class="header">
        <div class="logo">Agentor</div>
        <nav>
          ${isLoggedIn
        ? '<a href="/dashboard" data-link class="btn btn-primary btn-md">Dashboard</a>'
        : '<a href="/login" data-link class="btn btn-secondary btn-md">Log In</a>'
      }
        </nav>
      </header>

      <div class="notice">
        <h3>Alpha</h3>
        <p>Agentor is currently in Alpha testing. Please request access via our contact form below, if you would like to take part in the test.</p>
      </div>
      
      <main class="hero">
        <h1>Your Links. Your Brand. One Page.</h1>
        <p class="subtitle">
          Create a simple but sophisticated landing page with all your important links. 
          Share your brand, content, and connections with a single URL.
        </p>
        <a href="${isLoggedIn ? '/dashboard' : '/login'}" data-link class="btn btn-primary btn-lg">
          ${isLoggedIn ? 'Go to Dashboard' : 'Get Started Free'}
        </a>
      </main>
      
      <section class="features">
        <div class="feature">
          <div class="feature-icon">🔗</div>
          <h3>Link Management</h3>
          <p>Add, edit, and reorder your links with ease. Show what matters most.</p>
        </div>
        <div class="feature">
          <div class="feature-icon">🎨</div>
          <h3>Custom Branding</h3>
          <p>Match your page to your brand with custom colors and images.</p>
        </div>
        <div class="feature">
          <div class="feature-icon">📱</div>
          <h3>QR Codes</h3>
          <p>Generate QR codes for easy sharing on print materials.</p>
        </div>
      </section>
      <!--
      <section class="pricing">
        <div class="header">  
          <h2>Simple Pricing</h2>
          <p>Get started for free. Upgrade anytime for more features.</p>
        </div>
        <div class="plans">
          <div class="plan">
            <h3>Free Plan</h3>
            <p class="price">$0 / month</p>
            <ul class="features-list">
              <li>Basic link management</li>
              <li>Customizable page</li>
              <li>QR code generation</li>
            </ul>
            <a href="/signup" data-link class="btn btn-primary btn-lg">Sign Up for Free</a>
          </div>
          <div class="plan">
            <h3>Pro Plan</h3>
            <p class="price">$9.99 / month</p>
            <ul class="features-list">
              <li>Advanced link analytics</li>
              <li>Priority support</li>
              <li>More customization options</li>
            </ul>
            <a href="/purchase" data-link class="btn btn-primary btn-lg">Upgrade to Pro</a>
          </div>
          <div class="plan">
            <h3>Enterprise Plan</h3>
            <p class="price">Contact Us</p>
            <ul class="features-list">
              <li>Custom solutions</li>
              <li>Dedicated account manager</li>
              <li>24/7 support</li>
            </ul>
            <a href="#contactForm" data-link class="btn btn-primary btn-lg">Contact Sales</a>
          </div>
        </div>
      </section>

      <section class="privacy">
        <div class="header">
          <h2>Your Privacy Matters</h2>
          <p>We respect your privacy and are committed to protecting your data. Read our <a href="/privacy" data-link>Privacy Policy</a> to learn more.</p>
        </div>
        <div class="badges">
          <div class="badge">
            <h3>Privacy Aware</h3>
            <p>We adhere to strict data protection and privacy regulations as required per country where we offer our services. We default to the most conservative and strictest privacy polices by default.</p>
          </div>
          <div class="badge">
            <h3>Secure Data Storage</h3>
            <p>Your data is stored securely following industry-standard practices. Data is regularly backed up and security standards are audited frequently. We take the responsibilty of keeping your information stored on our system seriously, working to esnure your data safe and secure.</p>
          </div>
          <div class="badge">
            <h3>No Third Party Sharing</h3>
            <p>We do not share your personal data with third parties without your consent. At times in order provide functionality to you, we may need to interact with third party services. We will never share any information with them without your express consent. All our extended tools are opt-in by default, meaning you will never be opted into sharing information without your knowledge.</p>
          </div>
          <div class="badge">
            <h3>No Automatic AI</h3>
            <p>We will never automatically opt any user into AI related tooling or features we may add to the application in future. You will alway be asked if you would like to opt-in and your consent will always be needed.</p>
          </div>
          <div class="badge">
            <h3>AI Scraping & Tracking </h3>
            <p>We prevent thrid party AIs from scraping the your pages and links by default. Should you want AI to learn about your pages and links, you will be able to opt-in via your account dashboard, should you so choose.</p>
          </div>
        </div>
      </section>
      -->
      <section class="contact-us">
        <div class="header">
        <h2>Get in Touch</h2>
        <p>If you have any questions, feel free to contact us using the form below. We're here to help!</p>
        </div>
        <div class="contact-form-container">
        <form class="contact-form" id="contactForm">
          <div class="form-group">
            <label for="name">Name</label>
            <input type="text" id="name" name="name" placeholder="Your Name" maxlength="120" autocomplete="name" value="${escapeAttribute(this.contactName)}" required>
          </div>
          <div class="form-group">
            <label for="email">Email</label>
            <input type="email" id="email" name="email" placeholder="Your Email" maxlength="254" autocomplete="email" value="${escapeAttribute(this.contactEmail)}" required>
          </div>
          <div class="form-group">
            <label for="message">Message</label>
            <textarea id="message" name="message" placeholder="Your Message" maxlength="5000" autocomplete="off" required>${escapeHtml(this.contactMessage)}</textarea>
          </div>
          ${this.contactError ? `<p class="contact-status contact-error" role="alert" aria-atomic="true">${escapeHtml(this.contactError)}</p>` : ''}
          ${this.contactSuccess ? `<p class="contact-status contact-success" role="status" aria-live="polite" aria-atomic="true">${escapeHtml(this.contactSuccess)}</p>` : ''}
          <button type="submit" class="btn btn-primary" ${this.isSubmitting ? 'disabled' : ''}>
            ${this.isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
        </div>
      </section>
      
      <footer class="footer">
        ${new Date().getFullYear()} © Agentor by Oddlife Designs (pty) ltd. All rights reserved.
      </footer>
    `;
  }

  protected afterRender(): void {
    const form = this.$('#contactForm') as HTMLFormElement | null;
    form?.addEventListener('submit', (event) => this.handleContactSubmit(event));
  }

  private async handleContactSubmit(event: Event): Promise<void> {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    this.contactName = String(formData.get('name') || '');
    this.contactEmail = String(formData.get('email') || '');
    this.contactMessage = String(formData.get('message') || '');

    const name = sanitizePlainText(this.contactName, 120);
    const email = sanitizePlainText(this.contactEmail, 254).toLowerCase();
    const message = sanitizeMessageBody(this.contactMessage, 5000);

    this.contactError = '';
    this.contactSuccess = '';

    if (!name) {
      this.contactError = 'Please enter your name.';
      this.render();
      this.afterRender();
      return;
    }

    if (!email || !validateEmail(email)) {
      this.contactError = 'Please enter a valid email address.';
      this.render();
      this.afterRender();
      return;
    }

    if (!message) {
      this.contactError = 'Please enter a message.';
      this.render();
      this.afterRender();
      return;
    }

    this.isSubmitting = true;
    this.render();
    this.afterRender();

    const result = await api.sendContactMessage({ name, email, message });

    this.isSubmitting = false;
    if (!result.success) {
      this.contactError = result.error || 'Unable to send your message right now. Please try again.';
      this.render();
      this.afterRender();
      return;
    }

    this.contactSuccess = 'Thanks for your message. We will get back to you soon.';
    this.contactName = '';
    this.contactEmail = '';
    this.contactMessage = '';
    this.render();
    this.afterRender();
  }
}

customElements.define('home-page', HomePage);
